import React, {
  FC,
  useCallback,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useMemo,
  Fragment,
} from "react";

import classNames from "classnames";

import { StyledDataTable } from "./Styles";
import { ITableProps, Table, Tbody, Thead } from "./Table";
import { ISyntheticEvent } from "../../types";
import CollapseAllIcon from "../Icons/collection/CollapseAll";
import CollapseExpandSingleIcon from "../Icons/collection/CollapseExpandSingle";
import ExpandAllIcon from "../Icons/collection/ExpandAll";
import SortIcon from "../Icons/collection/SortIcon";
import { SkeletonLoader } from "../Loaders";

export enum SortDirection {
  None = "none",
  Asc = "asc",
  Desc = "desc",
}

interface IHeaderDefinition {
  key: string;
  label?: string;
  sortable?: boolean;
  renderCallback?: (row: object, key?: string, index?: number) => ReactNode;
  style?: object;
  hideCollapsed?: boolean;
}

export interface IDataTableProps extends ITableProps {
  /** Object of header name, sort and style. */
  header: IHeaderDefinition[];
  /** Object of rows. */
  rows?: object[];
  /** CSS classes. */
  className?: string;
  /** Name of row by which we are sorting. */
  sortBy?: string;
  /** Direction by which we are sorting. */
  sortDirection?: SortDirection;
  /** Callback for sorting. */
  onSortCallback?: (sortKey: string, sortDirection: SortDirection) => void;
  /** Switch for column highlighting. */
  columnHighlight?: boolean;
  /** CSS classes for thead. */
  theadClass?: string;
  /** Column by which is data-table grouped. */
  groupBy?: string;
  /** No results Callback. */
  noResultsCallback?: () => ReactNode;
  /** Header of grouped list in data-table. */
  groupHead?: (row: object, index: string | null) => ReactNode;
  /** Grouped rows can be collapsed */
  isCollapsible?: boolean;
  /** Value to replace when column is empty */
  emptyValue?: object;
  /** loading indicator */
  loading?: boolean;
  /** loading rows */
  loadingRows?: number;
}

const groupByKey = (items: object[], key: string): object => {
  if (items[0] instanceof Array) {
    items = items[0];
  }
  return items.reduce((result: object, item: object) => {
    return {
      ...result,
      [item[key] !== null ? " " + item[key] : item[key]]: [
        ...(result[item[key] !== null ? " " + item[key] : item[key]] || []),
        item,
      ],
    };
  }, {});
};

const initialCollapsed = (rows: object[], groupBy?: string): object => {
  if (groupBy) {
    const groupedData = groupByKey(rows, groupBy);
    const keys = Object.keys(groupedData);
    const collapsedKeys = {};
    keys.forEach((key) => {
      collapsedKeys[key] = true;
    });
    return collapsedKeys;
  }
  return {};
};

export const DataTable: FC<IDataTableProps> = ({
  header,
  rows = [],
  className,
  sortBy,
  sortDirection = SortDirection.None,
  onSortCallback,
  theadClass,
  noResultsCallback,
  groupBy,
  groupHead = (): null => null,
  isCollapsible,
  emptyValue,
  loading,
  loadingRows = 7,
  ...args
}) => {
  const [columnDirection, setColumnDirection] = useState(sortDirection);
  const [active, setActive] = useState(sortBy);
  const [hovered, setHovered] = useState<string | null>();
  const [collapsed, setCollapsed] = useState(() => {
    return initialCollapsed(rows, groupBy);
  });
  const sort = (
    sortByKey: string,
    direction: SortDirection,
    rowsData: Record<string, string>[] | object
  ): object => {
    if (direction === SortDirection.None) {
      return rowsData;
    }

    if (rowsData instanceof Array)
      return rowsData.sort((a: object, b: object) => {
        if (direction === SortDirection.Asc) {
          if (a[sortByKey] === null) {
            return 1;
          }
          if (b[sortByKey] === null) {
            return -1;
          }
        }
        if (a[sortByKey] === null) {
          return -1;
        }
        if (b[sortByKey] === null) {
          return 1;
        }
        if (typeof a[sortByKey] === "number") {
          if (direction === SortDirection.Asc) {
            return a[sortByKey] - b[sortByKey];
          }
          return b[sortByKey] - a[sortByKey];
        }

        const first = a[sortByKey].toString();
        const second = b[sortByKey].toString();

        if (direction === SortDirection.Asc) {
          return first.localeCompare(second);
        }

        return second.localeCompare(first);
      });
    return {};
  };

  const initialData = useCallback(() => {
    if (sortBy && !groupBy) {
      return sort(sortBy, sortDirection, rows);
    }
    if (sortBy && groupBy) {
      sort(sortBy, sortDirection, rows);
      return groupByKey(rows, groupBy);
    }
    if (!sortBy && groupBy) {
      return groupByKey(rows, groupBy);
    }
    return rows;
  }, [groupBy, rows, sortBy, sortDirection]);

  const [data, setData] = useState<Record<string, string> | object>(
    initialData
  );

  const getNextSortDirection = (
    previousDirection: SortDirection,
    sameColumn: boolean
  ): SortDirection => {
    if (!sameColumn) {
      return SortDirection.Asc;
    }
    if (previousDirection === SortDirection.Asc) {
      return SortDirection.Desc;
    }
    return SortDirection.Asc;
  };

  const sortColumn = useCallback(
    (event: ISyntheticEvent) => {
      const index = event.currentTarget.dataset.index;
      if (index && header[index] && header[index].sortable) {
        const direction = getNextSortDirection(
          columnDirection,
          header[index].key === active
        );
        setActive(header[index].key);
        setColumnDirection(direction);
        if (groupBy) {
          let grouped: object = {
            ...data,
          };

          if (groupBy !== header[index].key) {
            Object.keys(grouped).map((key: string) => {
              grouped[key] = sort(header[index].key, direction, grouped[key]);
            });
          } else {
            if (rows instanceof Object) {
              grouped = sort(header[index].key, direction, rows);
              grouped = groupByKey([grouped], groupBy);
            }
          }
          setData(grouped);
        } else {
          setData(sort(header[index].key, direction, data));
        }
        if (onSortCallback) {
          onSortCallback(header[index].key, direction);
        }
      }

      return null;
    },
    [
      setData,
      data,
      columnDirection,
      setColumnDirection,
      header,
      active,
      setActive,
      onSortCallback,
      groupBy,
      rows,
    ]
  );

  const cellMouseOver = useCallback((e: ISyntheticEvent) => {
    if (e.target instanceof HTMLElement) {
      if (e.target.dataset.key) {
        setHovered(e.target.dataset.key);
      }
    }
  }, []);

  const cellMouseOut = useCallback(() => {
    setHovered(null);
  }, []);

  useEffect(() => {
    setData(initialData());
    setCollapsed(initialCollapsed(rows, groupBy));
  }, [rows, groupBy, initialData]);

  const renderNoResults = useCallback(() => {
    return (
      <tr>
        <td
          colSpan={header.length}
          className={classNames({ "text-center": !noResultsCallback })}
        >
          {noResultsCallback ? noResultsCallback() : "There is no data."}
        </td>
      </tr>
    );
  }, [header, noResultsCallback]);

  const tableRef = useRef<HTMLTableElement>(null);

  const overlayStyles = (): number | undefined => {
    if (tableRef.current) {
      return tableRef.current.clientHeight + 5;
    }
  };

  const renderCell = useCallback(
    (h: IHeaderDefinition, row: object, colIndex: number) => {
      if (h.renderCallback) {
        return h.renderCallback(row, h.key, colIndex);
      }
      if (row[h.key] === null && emptyValue) {
        return <i className="opacity-75">{emptyValue[h.key]}</i>;
      }
      return row[h.key];
    },
    [emptyValue]
  );

  const renderRow = useCallback(
    (row: object, rowIndex: number) => {
      return (
        <tr
          key={rowIndex}
          data-index={rowIndex}
          data-direction="asc"
          className="c-table__row"
          onMouseOver={cellMouseOver}
          onMouseLeave={cellMouseOut}
        >
          {header.map((h: IHeaderDefinition, colIndex: number) => {
            return (
              <td
                key={colIndex}
                className={classNames(`c-table__cell c-data-table__item`, {
                  hovered: hovered === h.key,
                })}
                data-key={h.key}
                style={header[colIndex].style ? header[colIndex].style : {}}
              >
                {renderCell(h, row, colIndex)}
              </td>
            );
          })}
        </tr>
      );
    },
    [cellMouseOut, cellMouseOver, header, hovered, renderCell]
  );

  const toggleCollapse = useCallback((event) => {
    const collapseKey = event.currentTarget.dataset.groupkey;
    setCollapsed((prevState) => {
      const newState = { ...prevState };
      newState[collapseKey] = !prevState[collapseKey];
      return newState;
    });
  }, []);

  const renderPlaceholders = useMemo(() => {
    const placeholders = Array(loadingRows)
      .fill({})
      .map((_, index) => {
        return (
          <tr
            key={index}
            className="c-table__row"
            onMouseOver={cellMouseOver}
            onMouseLeave={cellMouseOut}
          >
            {header.map((col, colIndex) => {
              return (
                <td
                  key={colIndex}
                  className={classNames(`c-table__cell c-data-table__item`, {
                    hovered: hovered === col.key,
                  })}
                  data-key={col.key}
                >
                  <SkeletonLoader />
                </td>
              );
            })}
          </tr>
        );
      });

    return <Tbody>{placeholders}</Tbody>;
  }, [cellMouseOut, cellMouseOver, header, hovered, loadingRows]);

  const renderRows = useMemo(() => {
    if (
      (data && data instanceof Array && data.length > 0) ||
      (typeof data === "object" && Object.keys(data).length > 0)
    ) {
      if (groupBy) {
        const keys = Object.keys(data);
        return keys.map((key: string, i: number) => {
          return (
            <Fragment key={key}>
              {isCollapsible && (
                <Tbody className="c-table__collapse_body">
                  <tr>
                    <td
                      data-groupkey={key}
                      onClick={toggleCollapse}
                      className="c-table__body__icon c-table--toggle-collapsed"
                    >
                      <CollapseExpandSingleIcon
                        className={classNames({
                          "c-table__collapse_icon--expanded": !collapsed[key],
                          "c-table__collapse_icon--collapsed": collapsed[key],
                        })}
                      />
                    </td>
                  </tr>
                </Tbody>
              )}
              <Tbody
                className={classNames("c-table__grouped__head", `head_${i}`, {
                  "c-table__grouped__head--expanded":
                    isCollapsible && !collapsed[key],
                  "c-table__grouped__head--collapsed":
                    isCollapsible && collapsed[key],
                  last: i === keys.length - 1,
                  first: i === 0,
                })}
              >
                {groupHead(
                  data[key],
                  keys[i] !== "null" ? keys[i].trim() : null
                )}
              </Tbody>
              <Tbody
                className={classNames(`c-table__group c-table_group_${i}`, {
                  hidden: collapsed[key],
                })}
              >
                {data[key].map((row: object, index: number) => {
                  return renderRow(row, index);
                })}
              </Tbody>
            </Fragment>
          );
        });
      }
      return (
        <Tbody>
          {data instanceof Array &&
            data.map((row: object, rowIndex: number) => {
              return renderRow(row, rowIndex);
            })}
        </Tbody>
      );
    }
    return renderNoResults();
  }, [
    data,
    groupBy,
    renderRow,
    groupHead,
    collapsed,
    isCollapsible,
    renderNoResults,
    toggleCollapse,
  ]);

  const allGroupsCollapsed = useMemo(() => {
    return Object.keys(collapsed).every((key) => {
      return collapsed[key];
    });
  }, [collapsed]);

  const hasCollapsed = useMemo(() => {
    return Object.keys(collapsed).some((key) => {
      return collapsed[key];
    });
  }, [collapsed]);

  const toggleAllCollapsed = useCallback(() => {
    if (hasCollapsed) {
      setCollapsed((prevState) => {
        const newState = { ...prevState };
        Object.keys(newState).forEach((key) => {
          newState[key] = false;
        });
        return newState;
      });
    } else {
      setCollapsed((prevState) => {
        const newState = { ...prevState };
        Object.keys(newState).forEach((key) => {
          newState[key] = true;
        });
        return newState;
      });
    }
  }, [hasCollapsed]);

  return (
    <StyledDataTable
      as={Table}
      innerRef={tableRef}
      className={classNames("c-data-table", className, {
        "c-table__grouped": groupBy,
        "c-table__collapsible": isCollapsible,
        "padding-b-0": groupBy && collapsed[Object.keys(data).slice(-1)[0]],
      })}
      {...args}
    >
      <Thead className={theadClass}>
        {header.length > 0 && (
          <tr className="c-table__row">
            {header.map((h: IHeaderDefinition, index: number) => {
              return (
                <th
                  key={h.key}
                  className={classNames(`c-table__cell`, {
                    "c-table__cell--hovered": hovered === h.key,
                    "c-table__head--hidden":
                      groupBy && allGroupsCollapsed && h.hideCollapsed,
                  })}
                  onMouseOver={cellMouseOver}
                  onMouseLeave={cellMouseOut}
                  data-key={h.key}
                  style={h.style}
                >
                  {isCollapsible && groupBy && index === 0 && (
                    <span
                      className="c-table__head__icon-wrapper"
                      onClick={toggleAllCollapsed}
                      data-test="toggle-collapse"
                    >
                      {hasCollapsed ? (
                        <ExpandAllIcon className="c-table__head__icon-wrapper__icon" />
                      ) : (
                        <CollapseAllIcon className="c-table__head__icon-wrapper__icon" />
                      )}
                    </span>
                  )}
                  {hovered === h.key && (
                    <div
                      className="c-table__overlay"
                      style={{ height: overlayStyles() + `px` }}
                    />
                  )}
                  <div
                    onClick={sortColumn}
                    data-index={index}
                    className={classNames("c-table__icon-wrapper", {
                      "cursor-pointer": h.sortable === true,
                    })}
                  >
                    <span className="c-table__label">{h.label}</span>
                    {h.sortable && (
                      <SortIcon
                        width="10px"
                        height="10px"
                        className={classNames("c-table__icon", {
                          "c-table__icon--inactive": h.key !== active,
                          "c-table__icon--active": h.key === active,
                          "c-table__icon--asc":
                            columnDirection === SortDirection.Asc &&
                            h.key === active,
                          "c-table__icon--desc":
                            columnDirection === SortDirection.Desc &&
                            h.key === active,
                        })}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        )}
      </Thead>

      {loading ? renderPlaceholders : renderRows}
    </StyledDataTable>
  );
};

DataTable.displayName = "DataTable";
